import { useCallback, useEffect, useRef } from "react";
import { Modal as RNModal, Platform, type ModalProps } from "react-native";

import { enterModal } from "@/state/confirm-store";

/**
 * How long iOS may take to finish dismissing before we stop waiting for it.
 *
 * This is a CEILING, not a measurement, and it is not the mechanism: on iOS the
 * count is released by `onDismiss`, which React Native fires from the
 * `dismissViewControllerAnimated:` COMPLETION handler
 * (RCTModalHostViewComponentView.mm) — genuinely "UIKit has finished". The
 * timer exists only so a dismissal whose event never arrives cannot hold every
 * later dialog forever; React Native's own Modal.js says as much ("the view
 * will be destroyed before the callback is fired"), which is exactly the
 * unmount-while-visible case. UIKit's stock modal transitions run ~250-400ms,
 * so this leaves better than 2x headroom and is only ever reached when the
 * real signal is lost.
 */
const IOS_DISMISS_CEILING_MS = 700;

/**
 * Every app-owned native Modal, so that something knows one is on screen.
 *
 * Exported as `Modal` as well as `AppModal` so adopting it is an import-path
 * change and nothing else — no JSX churn across twenty-odd screens, which is
 * how a mechanical migration stays reviewable.
 *
 * The only behaviour added is the registration below. Props, layout, animation
 * and platform behaviour are React Native's, untouched: this must not become a
 * place where modals quietly acquire opinions. Passing `onDismiss` through to
 * RNModal costs nothing — Modal.js wires the native callback unconditionally
 * and only forwards it to the prop on iOS — so this changes no presentation.
 *
 * See `state/confirm-store.ts` for why this exists at all.
 */
export function AppModal({ visible, onDismiss, ...rest }: ModalProps) {
  /**
   * A count that has been given up by React but not yet by UIKit. Held per
   * presentation rather than per component so a modal that closes and reopens
   * before the first dismissal finishes cannot have the older entry release
   * the newer one's count.
   */
  const held = useRef<{
    leave: () => void;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);

  const release = useCallback(() => {
    const entry = held.current;
    if (!entry) return;
    held.current = null;
    if (entry.timer) clearTimeout(entry.timer);
    entry.leave();
  }, []);

  useEffect(() => {
    // `visible` is the presentation, not mounting: RN Modals are routinely
    // mounted-but-hidden, and counting those would report the whole app as
    // permanently modal.
    if (!visible) return undefined;
    const leave = enterModal();

    return () => {
      // Android and web have no second presentation to wait for — the view is
      // gone when React says it is — and `onDismiss` is iOS-only in React
      // Native, so waiting for it anywhere else would hold the count forever
      // and silently stop the app showing dialogs at all.
      if (Platform.OS !== "ios") {
        leave();
        return;
      }

      // iOS: React saying "not visible" only starts the dismissal. UIKit will
      // not present another modal until it finishes, so releasing here would
      // let the queued dialog present mid-animation — the same class of bug
      // this whole mechanism exists to prevent. Hold the count until the
      // dismissal actually completes.
      const entry: {
        leave: () => void;
        timer: ReturnType<typeof setTimeout> | null;
      } = { leave, timer: null };

      entry.timer = setTimeout(() => {
        // Only ever clear our OWN entry; a later presentation may own the ref.
        if (held.current === entry) held.current = null;
        leave();
      }, IOS_DISMISS_CEILING_MS);

      held.current = entry;
    };
  }, [visible]);

  // Deliberately no unmount cleanup that cancels the timer. Unmounting while
  // visible is precisely when `onDismiss` may never arrive, so the timer must
  // outlive the component or the count leaks and every later dialog is held.
  // It captures `leave` and nothing else, so it cannot touch a dead component.

  const handleDismiss = useCallback(() => {
    release();
    onDismiss?.();
  }, [release, onDismiss]);

  return <RNModal visible={visible} {...rest} onDismiss={handleDismiss} />;
}

export { AppModal as Modal };
